import { create } from "zustand";
import { persist } from "zustand/middleware";

// Onboarding state. `completedTours` is persisted to localStorage so that once
// a user finishes OR skips a tour it never shows again — even before the
// backend `has_completed_onboarding` flag round-trips, and even offline.
export const useOnboardingStore = create(
  persist(
    (set, get) => ({
      isOpen: false,
      currentTour: null,
      currentStepIndex: 0,
      completedTours: [],

      // Open a tour, but never re-open one already finished/skipped.
      openTour: (tourId) => {
        if (get().completedTours.includes(tourId)) return;
        set({ isOpen: true, currentTour: tourId, currentStepIndex: 0 });
      },

      nextStep: () =>
        set((state) => ({ currentStepIndex: state.currentStepIndex + 1 })),

      prevStep: () =>
        set((state) => ({
          currentStepIndex:
            state.currentStepIndex > 0 ? state.currentStepIndex - 1 : 0,
        })),

      // Mark the active tour done and close. Used by both Skip and Finish so
      // either action permanently dismisses the tour.
      completeTour: (tourId) =>
        set((state) => {
          const id = tourId || state.currentTour;
          return {
            isOpen: false,
            currentTour: null,
            completedTours: id
              ? [...new Set([...state.completedTours, id])]
              : state.completedTours,
          };
        }),

      // Back-compat aliases — both now permanently dismiss the tour.
      closeTour: () => get().completeTour(),
      finishTour: (tourId) => get().completeTour(tourId),

      // Forget a tour so it can be replayed (e.g. from Settings).
      resetTour: (tourId) =>
        set((state) => ({
          isOpen: false,
          completedTours: state.completedTours.filter((t) => t !== tourId),
        })),

      hasCompleted: (tourId) => get().completedTours.includes(tourId),
    }),
    {
      name: "aw-onboarding",
      // Only the completion list is persisted — never the transient open state.
      partialize: (state) => ({ completedTours: state.completedTours }),
    },
  ),
);
